import { useMemo, useRef, useState } from 'react';
import { Braces } from 'lucide-react';
import type { CallFact, ComponentCallDirection } from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { previewLine } from './graph';

/**
 * One spoken line, in English and Spanish, with the facts it may use.
 *
 * WHAT THE PICKER OFFERS IS THE WHOLE LIST. A line can only speak what the
 * call already knows (`callFacts`, with an invented example each) plus the
 * placeholders its own step fills in (`vars`) — nothing else renders. Some
 * lines see only their step's placeholders: a callback read-back must never
 * reach a chart detail for a caller not yet verified, so there the call's
 * facts are not offered at all. The note under the list says why a referral's
 * diagnosis is never in it.
 */

export interface LineWords {
  en: string;
  es: string;
}

interface LineEditorProps {
  words: LineWords;
  onCommit: (words: LineWords) => void;
  facts: readonly CallFact[];
  factsNote: string;
  direction: ComponentCallDirection;
  /** Placeholders this line's step fills in when it speaks it. */
  vars?: readonly string[];
  /** False when only `vars` are in scope. */
  callFacts?: boolean;
  disabled?: boolean;
  error?: string;
  /** Does not block a publish: shown quietly. */
  warning?: string;
  /** What a Spanish caller hears while the Spanish box is empty. */
  spanishFallback?: string;
}

type Lang = keyof LineWords;
const LANGS: { id: Lang; label: string }[] = [
  { id: 'en', label: 'English' },
  { id: 'es', label: 'Spanish' },
];

export function LineEditor({
  words,
  onCommit,
  facts,
  factsNote,
  direction,
  vars = [],
  callFacts = true,
  disabled,
  error,
  warning,
  spanishFallback = 'Spanish callers hear the English line until this is set',
}: LineEditorProps): React.ReactElement {
  const [local, setLocal] = useState<LineWords>(words);
  const [seen, setSeen] = useState<LineWords>(words);
  // A save elsewhere (or a reload) replaced the words: follow it.
  if (seen.en !== words.en || seen.es !== words.es) {
    setSeen(words);
    setLocal(words);
  }
  const refs = { en: useRef<HTMLTextAreaElement>(null), es: useRef<HTMLTextAreaElement>(null) };
  const lastFocused = useRef<Lang>('en');
  // Closed on every pick: two quick picks would be two saves on one revision.
  const [pickerOpen, setPickerOpen] = useState(false);

  const offered = useMemo(
    () => (callFacts ? facts.filter((f) => f.directions.includes(direction)) : []),
    [facts, direction, callFacts]
  );
  const examples = useMemo(() => new Map(offered.map((f) => [f.path, f.example])), [offered]);

  const commit = (next: LineWords): void => {
    if (next.en === words.en && next.es === words.es) return;
    onCommit(next);
  };

  const insert = (path: string): void => {
    setPickerOpen(false);
    const lang = lastFocused.current;
    const el = refs[lang].current;
    const token = `{{${path}}}`;
    const text = local[lang];
    const at = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? at;
    const next = { ...local, [lang]: text.slice(0, at) + token + text.slice(end) };
    setLocal(next);
    commit(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + token.length, at + token.length);
    });
  };

  return (
    <div className="space-y-2">
      {LANGS.map(({ id, label }) => (
        <div key={id} className="space-y-1">
          <div className="text-xs text-muted-foreground">{label}</div>
          <Textarea
            ref={refs[id]}
            value={local[id]}
            rows={2}
            disabled={disabled}
            placeholder={id === 'es' ? spanishFallback : ''}
            onFocus={() => {
              lastFocused.current = id;
            }}
            onChange={(e) => setLocal({ ...local, [id]: e.target.value })}
            onBlur={() => commit(local)}
          />
          {local[id].includes('{{') && (
            <p className="text-xs text-muted-foreground">
              Sounds like: {previewLine(local[id], examples)}
            </p>
          )}
        </div>
      ))}
      {error && <p className="text-xs text-destructive">{error}</p>}
      {warning && <p className="text-xs text-muted-foreground">{warning}</p>}
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" disabled={disabled}>
            <Braces className="mr-2 h-3.5 w-3.5" />
            Insert a fact
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="start">
          <div className="max-h-80 overflow-y-auto p-1">
            {vars.length > 0 && (
              <div className="px-2 pb-1 pt-2 text-xs font-medium text-muted-foreground">
                Filled in by this step
              </div>
            )}
            {vars.map((v) => (
              <FactRow key={v} path={v} label={v} onPick={insert} />
            ))}
            {offered.length > 0 && (
              <div className="px-2 pb-1 pt-2 text-xs font-medium text-muted-foreground">
                Known before the call starts
              </div>
            )}
            {offered.map((f) => (
              <FactRow
                key={f.path}
                path={f.path}
                label={f.label}
                example={f.example}
                onPick={insert}
              />
            ))}
            {!callFacts && (
              <p className="px-2 py-2 text-xs text-muted-foreground">
                This line can use only what its step fills in, so no chart detail reaches a caller
                who is not yet verified.
              </p>
            )}
          </div>
          <p className="border-t px-3 py-2 text-xs text-muted-foreground">{factsNote}</p>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function FactRow({
  path,
  label,
  example,
  onPick,
}: {
  path: string;
  label: string;
  example?: string;
  onPick: (path: string) => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      className="flex w-full items-baseline justify-between gap-3 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
      onClick={() => onPick(path)}
    >
      <span className="min-w-0">
        <span className="block truncate">{label}</span>
        <span className="block truncate font-mono text-xs text-muted-foreground">{`{{${path}}}`}</span>
      </span>
      {example && <span className="shrink-0 text-xs text-muted-foreground">{example}</span>}
    </button>
  );
}
